// Color helpers: hex <-> hsl, basic classifiers for filters

export function hexToRgb(hex) {
  const clean = hex.replace("#", "");
  const bigint = parseInt(clean, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return { r, g, b };
}

export function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l; l = (max + min) / 2;

  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h *= 60;
  }
  return { h, s, l };
}

// Professional color conversion: RGB to XYZ (CIE 1931)
export function rgbToXyz(r, g, b) {
  // Normalize to 0-1
  r = r / 255;
  g = g / 255;
  b = b / 255;

  // Apply gamma correction (sRGB)
  r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
  g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
  b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;

  // Observer = 2°, Illuminant = D65
  const x = r * 0.4124564 + g * 0.3575761 + b * 0.1804375;
  const y = r * 0.2126729 + g * 0.7151522 + b * 0.0721750;
  const z = r * 0.0193339 + g * 0.1191920 + b * 0.9503041;

  return { x: x * 100, y: y * 100, z: z * 100 };
}

// Professional color conversion: XYZ to LAB (CIELAB)
export function xyzToLab(x, y, z) {
  // Reference white D65
  const xn = 95.047;
  const yn = 100.000;
  const zn = 108.883;

  x = x / xn;
  y = y / yn;
  z = z / zn;

  const fx = x > 0.008856 ? Math.pow(x, 1/3) : (7.787 * x + 16/116);
  const fy = y > 0.008856 ? Math.pow(y, 1/3) : (7.787 * y + 16/116);
  const fz = z > 0.008856 ? Math.pow(z, 1/3) : (7.787 * z + 16/116);

  const L = 116 * fy - 16;
  const a = 500 * (fx - fy);
  const b = 200 * (fy - fz);

  return { L, a, b };
}

// Convert RGB directly to LAB
export function rgbToLab(r, g, b) {
  const xyz = rgbToXyz(r, g, b);
  return xyzToLab(xyz.x, xyz.y, xyz.z);
}

// Professional Delta E (CIE 2000) - Industry standard for color difference
export function deltaE2000(lab1, lab2) {
  const { L: L1, a: a1, b: b1 } = lab1;
  const { L: L2, a: a2, b: b2 } = lab2;

  // Calculate C and h values
  const C1 = Math.sqrt(a1 * a1 + b1 * b1);
  const C2 = Math.sqrt(a2 * a2 + b2 * b2);
  const deltaL = L2 - L1;
  const deltaC = C2 - C1;
  const deltaA = a2 - a1;
  const deltaB = b2 - b1;

  // Simplified Delta E calculation (close approximation)
  const deltaH = Math.sqrt(deltaA * deltaA + deltaB * deltaB - deltaC * deltaC);

  const kL = 1.0;
  const kC = 1.0;
  const kH = 1.0;

  const SL = 1.0;
  const SC = 1 + 0.045 * C1;
  const SH = 1 + 0.015 * C1;

  const deltaE = Math.sqrt(
    Math.pow(deltaL / (kL * SL), 2) +
    Math.pow(deltaC / (kC * SC), 2) +
    Math.pow(deltaH / (kH * SH), 2)
  );

  return deltaE;
}

// Enhanced color analysis for professional matching
export function analyzeColor(hex) {
  const rgb = hexToRgb(hex);
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const lab = rgbToLab(rgb.r, rgb.g, rgb.b);

  // Color characteristics with more precise thresholds
  const isDark = hsl.l < 0.25;           // More strict dark threshold
  const isLight = hsl.l > 0.75;          // More strict light threshold  
  const isVeryLight = hsl.l > 0.85;      // NEW: Very light colors
  const isNeutral = hsl.s < 0.12;        // Slightly more strict neutral
  const isAlmostNeutral = hsl.s < 0.25;  // NEW: Almost neutral (like light grays)
  const isVibrant = hsl.s > 0.6 && hsl.l > 0.3 && hsl.l < 0.8;
  const isMuted = hsl.s < 0.4 && hsl.l > 0.2 && hsl.l < 0.8;

  // Dominant hue classification - more careful for low saturation
  const h = hsl.h;
  let dominantHue = 'neutral';
  if (!isNeutral && hsl.s > 0.15) { // Only classify hue if reasonably saturated
    if (h >= 0 && h < 30) dominantHue = 'red';
    else if (h >= 30 && h < 60) dominantHue = 'orange';
    else if (h >= 60 && h < 90) dominantHue = 'yellow';
    else if (h >= 90 && h < 150) dominantHue = 'green';
    else if (h >= 150 && h < 210) dominantHue = 'cyan';
    else if (h >= 210 && h < 270) dominantHue = 'blue';
    else if (h >= 270 && h < 330) dominantHue = 'purple';
    else dominantHue = 'red';
  }

  return {
    rgb,
    hsl,
    lab,
    isDark,
    isLight,
    isVeryLight,
    isNeutral,
    isAlmostNeutral,
    isVibrant,
    isMuted,
    dominantHue
  };
}

export function hexToHsl(hex) {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHsl(r, g, b);
}

// Filters
export function isWarm(hsl) {
  const h = hsl.h;
  return h < 90 || h > 330; // reds, oranges, yellows
}

export function isCool(hsl) {
  const h = hsl.h;
  return h >= 90 && h <= 270; // greens, blues, violets
}

export function isPastel(hsl) {
  // Pastels: light and gently saturated — wide enough to include dusty/muted pastels
  // not just candy-bright ones. l>0.60 catches medium-light tones, s<0.65 allows
  // visible colour without being vivid.
  return hsl.l > 0.60 && hsl.s > 0.05 && hsl.s < 0.65;
}

export function isVivid(hsl) {
  // Vivid: punchy and saturated but not neon — drop min saturation slightly
  // and widen lightness window so deep vivids aren't excluded.
  return hsl.s >= 0.55 && hsl.l >= 0.30 && hsl.l <= 0.80;
}

// Moody: desaturated, earthy, atmospheric darks — burgundy, dusty rose, muted olive,
// charcoal, rust, warm greys. Mid-to-dark range, low-to-medium saturation.
// Distinct from Night (which is cool + very dark) — Moody spans all hues but muted.
export function isMoody(hsl) {
  const s = hsl.s;
  const l = hsl.l;

  // Widen both axes: moody spans from almost-dark to mid-tone,
  // and from barely-saturated (dusty) to reasonably rich.
  if (l < 0.12 || l > 0.62) return false;
  if (s < 0.08 || s > 0.65) return false;

  return true;
}

export function isNeutral(hsl) {
  // Slightly wider — catches warm greys, stone tones, off-whites
  return hsl.s < 0.25;
}

// Retro: 70s/80s aesthetic - burnt oranges, mustard yellows, avocado greens, teals
// Medium saturation, medium lightness, specific hue ranges
export function isRetro(hsl) {
  const h = hsl.h;
  const s = hsl.s;
  const l = hsl.l;

  // Wider saturation and lightness so muted retro tones aren't missed
  if (s < 0.18 || s > 0.78) return false;
  if (l < 0.25 || l > 0.68) return false;

  const isRetroHue =
    (h >= 0 && h <= 45) ||     // Reds, oranges, burnt orange
    (h >= 45 && h <= 75) ||    // Mustard yellow, gold
    (h >= 75 && h <= 145) ||   // Avocado green, olive
    (h >= 170 && h <= 210);    // Teal, turquoise

  return isRetroHue;
}

// Vintage: Muted, desaturated, "aged" colors - faded pastels, dusty tones
// Low saturation, medium-to-light lightness
export function isVintage(hsl) {
  const s = hsl.s;
  const l = hsl.l;

  // Broaden both ranges — faded doesn't mean extremely light only
  if (s < 0.08 || s > 0.52) return false;
  if (l < 0.32 || l > 0.80) return false;

  return true;
}

// Neon: Bright, highly saturated, electric colors
// Very high saturation, medium-to-high lightness
export function isNeon(hsl) {
  const s = hsl.s;
  const l = hsl.l;

  // Slightly relax saturation floor and widen lightness — marker neons
  // aren't always perfectly electric but are still clearly bright/punchy.
  if (s < 0.65) return false;
  if (l < 0.40 || l > 0.78) return false;

  return true;
}

// Summer: Bright, warm, vibrant colors - yellows, oranges, hot pinks, turquoise
// High saturation, medium-to-high lightness, warm and cool bright tones
export function isSummer(hsl) {
  const h = hsl.h;
  const s = hsl.s;
  const l = hsl.l;

  if (s < 0.28) return false;
  if (l < 0.38 || l > 0.85) return false;

  const isSummerHue =
    (h >= 10 && h <= 85) ||    // Yellows, oranges, coral
    (h >= 285 && h <= 360) ||  // Hot pinks, magentas, fuchsia
    (h >= 160 && h <= 215);    // Turquoise, cyan, aqua

  return isSummerHue;
}

// Spring: Fresh, light, pastel colors - soft pinks, light greens, lavenders, light yellows
// Lower to medium saturation, lighter colors, delicate and fresh
export function isSpring(hsl) {
  const h = hsl.h;
  const s = hsl.s;
  const l = hsl.l;

  // Drop lightness floor — spring includes fresh mid-tones, not only near-whites
  if (s < 0.10 || s > 0.80) return false;
  if (l < 0.45 || l > 0.92) return false;

  const isSpringHue =
    (h >= 315 || h <= 25) ||   // Soft pinks, blush, rose
    (h >= 70 && h <= 175) ||   // Light greens, mint, jade
    (h >= 235 && h <= 315) ||  // Lavenders, lilac, mauve
    (h >= 40 && h <= 70);      // Light yellows, cream

  return isSpringHue;
}

// Autumn: Warm, earthy, rich colors - oranges, reds, browns, golden yellows, warm greens
// Medium saturation, medium lightness, warm earth tones
export function isAutumn(hsl) {
  const h = hsl.h;
  const s = hsl.s;
  const l = hsl.l;

  // Lower saturation floor catches muted/dusty autumn tones (dried leaves, bark)
  if (s < 0.15 || s > 0.85) return false;
  if (l < 0.20 || l > 0.72) return false;

  const isAutumnHue =
    (h >= 335 || h <= 75) ||   // Reds, oranges, golden yellows
    (h >= 75 && h <= 115);     // Olive greens, warm greens

  return isAutumnHue;
}

// Winter: Cool, crisp colors - icy blues, deep purples, cool grays, evergreen, cool tones
// Varied saturation, cool hue range, can be light or dark
export function isWinter(hsl) {
  const h = hsl.h;
  const s = hsl.s;

  // More forgiving - allow wider saturation range
  if (s > 0.85) return false; // Only exclude extremely vivid neons

  // Winter hues: evergreen, teals, icy blues, deep purples, cool grays
  const isWinterHue =
    (h >= 120 && h <= 260) ||  // Evergreen to blues to purples
    (h >= 260 && h <= 320);    // Deep purples, violets, cool magentas

  // For lower saturation (neutral/cool grays), be more inclusive
  if (s < 0.20) {
    // Cool grays and neutrals - allow wider range
    return true;
  }

  return isWinterHue;
}

// Day: Soft, bright, uplifting tones — warm yellows, sky blues, fresh greens, peachy pinks
// High lightness, moderate-to-high saturation, sunny hues
export function isDay(hsl) {
  const h = hsl.h;
  const s = hsl.s;
  const l = hsl.l;

  // Relax brightness and saturation floors — a soft sky blue or mint qualifies
  if (l < 0.48 || s < 0.18) return false;

  return (
    (h >= 20 && h <= 85)   ||  // Warm yellows, gold, peach
    (h >= 170 && h <= 230) ||  // Sky blues, cyan
    (h >= 85 && h <= 165)  ||  // Fresh greens, lime, mint
    (h >= 315 || h <= 20)      // Peachy pinks, soft rose
  );
}

// Night: Deep, moody tones — navy blues, purples, dark turquoise, muted pinks, golden accents
// Lower lightness, richer saturation, nocturnal hues
export function isNight(hsl) {
  const h = hsl.h;
  const s = hsl.s;
  const l = hsl.l;

  // Allow slightly lighter and less-saturated tones — dark teal/dusty navy qualify
  if (l > 0.58 || s < 0.14) return false;

  return (
    (h >= 185 && h <= 270) ||  // Navy, deep blues, midnight
    (h >= 270 && h <= 325) ||  // Deep purples, violet, dark magenta
    (h >= 145 && h <= 185) ||  // Dark teal, dark turquoise
    (h >= 30 && h <= 58)       // Golden/amber moonlight accents
  );
}

// Skin: full range of human skin tones — light ivory to deep brown/ebony
// Warm-to-neutral hues (reds/oranges/yellows), low-to-medium saturation, mid lightness
export function isSkin(hsl) {
  const h = hsl.h;
  const s = hsl.s;
  const l = hsl.l;

  // Skin hues span warm reds, oranges, yellow-oranges
  if (!(h >= 0 && h <= 45) && !(h >= 345 && h <= 360)) return false;

  // Saturation: low-to-medium (skin is never neon or grey)
  if (s < 0.08 || s > 0.65) return false;

  // Lightness: wide range to cover all skin tones from very light to very deep
  if (l < 0.18 || l > 0.88) return false;

  return true;
}

// Dark: intense, high-contrast darks — deep blacks, charcoals, dark purples,
// petrol blues, burgundy. Very low lightness across all hues.
export function isDark(hsl) {
  const s = hsl.s;
  const l = hsl.l;

  // Raise ceiling slightly so deep jewel tones and dark earthy colours qualify
  if (l > 0.32) return false;
  if (s < 0.05 && l > 0.15) return false;

  return true;
}

// Earth: grounded, natural tones — warm browns, terracotta, olive, forest greens,
// sandy beiges, slate blues, muddy teals. Muted saturation, mid-to-dark lightness.
export function isEarth(hsl) {
  const h = hsl.h;
  const s = hsl.s;
  const l = hsl.l;

  // Must have some colour — not pure grey, not neon
  if (s < 0.08 || s > 0.72) return false;

  // Not too light (no pastels), not pitch-black
  if (l < 0.18 || l > 0.72) return false;

  // Earth hues: warm browns/terracotta (0-45°), olive/forest greens (55-150°),
  // muted teals (155-210°), and slate/dusty blue-greens (200-240°)
  const isEarthHue =
    (h >= 0   && h <= 45)  ||  // Warm browns, terracotta, rust, sienna
    (h >= 45  && h <= 150) ||  // Olive, moss, forest, sage, khaki
    (h >= 150 && h <= 215) ||  // Muted teals, slate green, water blues
    (h >= 340 && h <= 360);    // Warm red-browns wrapping around

  return isEarthHue;
}

// Cottage: cozy, pastoral tones — dusty rose, sage green, warm cream, lavender mist,
// soft terracotta, aged linen. Think countryside florals and garden afternoons.
// Mid-light values, low-medium saturation, warm-cool balance tilted romantic.
export function isCottage(hsl) {
  const h = hsl.h;
  const s = hsl.s;
  const l = hsl.l;

  // Must be soft but not washed out — gentle saturation
  if (s < 0.06 || s > 0.62) return false;

  // Light to mid-tone: creamy pastels to muted mid-tones
  if (l < 0.38 || l > 0.88) return false;

  // Cottage hues: dusty rose/blush (330–20°), sage/moss green (85–155°),
  // soft lavender (250–310°), warm ochre/cream (30–80°)
  const isCottageHue =
    (h >= 330 || h <= 20)  ||  // Dusty rose, blush, warm pink
    (h >= 20  && h <= 80)  ||  // Cream, warm straw, soft ochre
    (h >= 85  && h <= 155) ||  // Sage, moss, herb green
    (h >= 250 && h <= 310);    // Soft lavender, muted violet

  return isCottageHue;
}

export function hueDistance(a, b) {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

// ─── Shade finder ────────────────────────────────────────────────────────────
// Score how well `candidate` works as a shade (darker companion) of `source`.
// Lower is better. For neutrals (low S), hue matching is relaxed and lightness
// drives the score. Returns null if candidate isn't darker than source.
export function shadeScore(source, candidate) {
  const src = typeof source === 'string' ? hexToHsl(source) : source;
  const cnd = typeof candidate === 'string' ? hexToHsl(candidate) : candidate;
  if (!src || !cnd) return null;

  // Convert L to 0–100 scale used in the spec (rgbToHsl returns 0–1)
  const srcL = src.l * 100;
  const cndL = cnd.l * 100;
  const lightDiff = srcL - cndL; // positive = candidate is darker

  if (lightDiff <= 0) return null; // not darker — not a shade

  const hDiff = hueDistance(src.h, cnd.h);
  const sDiff = Math.abs(src.s - cnd.s); // 0..1

  // Neutral source (very low saturation) — match by lightness, ignore hue mostly
  const neutralSource = src.s < 0.12;

  let score;
  if (neutralSource) {
    // For greys, prefer near-neutral candidates with the right lightness drop
    score = sDiff * 60 - lightDiff * 8;
  } else {
    score =
      hDiff * 2 +
      sDiff * 30 -
      lightDiff * 1.5 +
      (lightDiff < 5 ? 50 : 0) +
      (lightDiff > 40 ? 40 : 0);
  }

  return { score, hueDiff: hDiff, lightDiff, satDiff: sDiff };
}

// Quality tier from hue drift + lightness drop. Used for the "Excellent / Good /
// Approximate" badge in the UI.
export function shadeQuality(metrics) {
  if (!metrics) return 'approximate';
  const { hueDiff, lightDiff } = metrics;
  if (hueDiff <= 12 && lightDiff >= 6 && lightDiff <= 35) return 'excellent';
  if (hueDiff <= 25 && lightDiff >= 4 && lightDiff <= 45) return 'good';
  return 'approximate';
}

// Find shade chain for a source marker from a candidate pool.
// Returns an array of { marker, metrics, quality } of length up to `count`.
// Each step uses the previous marker as the new source so the chain progresses
// naturally (Source → Shadow 1 → Shadow 2…).
//
// Excludes blenders (code "0", "00", names containing "blender") and the source
// itself from candidates.
export function findShades(sourceMarker, candidates, count = 2) {
  if (!sourceMarker || !sourceMarker.hex) return [];

  const isBlender = (m) => {
    const name = (m.name || '').toLowerCase();
    if (name.includes('blender') || name.includes('colorless')) return true;
    const codes = [m.code, m.code_old, m.code_new]
      .filter(Boolean)
      .map((c) => String(c).trim().toUpperCase());
    return codes.some((c) => c === '0' || c === '00');
  };

  const sameMarker = (a, b) => {
    if (!a || !b) return false;
    if (a.hex && b.hex && a.hex.toLowerCase() === b.hex.toLowerCase() && a.brand === b.brand) {
      return true;
    }
    return false;
  };

  const chain = [];
  let current = sourceMarker;
  // Live pool, removing each picked marker so the chain doesn't repeat
  const pool = (candidates || []).filter((m) => m && m.hex && !isBlender(m) && !sameMarker(m, sourceMarker));

  for (let step = 0; step < count; step++) {
    let best = null;
    let bestIdx = -1;
    for (let i = 0; i < pool.length; i++) {
      const cand = pool[i];
      const metrics = shadeScore(current.hex, cand.hex);
      if (!metrics) continue;
      if (best == null || metrics.score < best.metrics.score) {
        best = { marker: cand, metrics, quality: shadeQuality(metrics) };
        bestIdx = i;
      }
    }
    if (!best) break;
    chain.push(best);
    pool.splice(bestIdx, 1);
    current = best.marker;
  }

  return chain;
}

// Edge-case classifier for source markers — drives UI hints.
// Returns one of: 'normal' | 'tooDark' | 'neutral' | 'extremeWhite' | 'extremeBlack'.
export function classifyShadeSource(hex) {
  if (!hex) return 'normal';
  const hsl = hexToHsl(hex);
  if (!hsl) return 'normal';
  const L = hsl.l * 100;
  if (L < 6) return 'extremeBlack';
  if (L > 96 && hsl.s < 0.08) return 'extremeWhite';
  if (L < 20) return 'tooDark';
  if (hsl.s < 0.12) return 'neutral';
  return 'normal';
}

// Find similar colors using Enhanced Euclidean distance
export function findSimilarColors(targetHex, markers, maxResults = 5) {
  const targetRgb = hexToRgb(targetHex);
  
  // Enhanced Euclidean distance with perceptual weighting for green vs gray
  const calculateEnhancedEuclideanDistance = (rgb1, rgb2) => {
    // Standard Euclidean distance
    const standardDistance = Math.sqrt(
      Math.pow(rgb1.r - rgb2.r, 2) +
      Math.pow(rgb1.g - rgb2.g, 2) +
      Math.pow(rgb1.b - rgb2.b, 2)
    );
    
    // Enhanced distance with perceptual weighting
    // Human eye is more sensitive to green, then red, then blue
    const perceptualDistance = Math.sqrt(
      Math.pow(rgb1.r - rgb2.r, 2) * 0.30 +  // Red weight
      Math.pow(rgb1.g - rgb2.g, 2) * 0.59 +  // Green weight (most important)
      Math.pow(rgb1.b - rgb2.b, 2) * 0.11    // Blue weight
    );
    
    // Additional hue-aware penalty to distinguish green from gray
    const hsl1 = rgbToHsl(rgb1.r, rgb1.g, rgb1.b);
    const hsl2 = rgbToHsl(rgb2.r, rgb2.g, rgb2.b);
    
    let hueBonus = 0;
    
    // Special handling for green vs gray distinction
    const isGreen1 = (hsl1.h >= 90 && hsl1.h <= 150) && hsl1.s > 0.1;
    const isGreen2 = (hsl2.h >= 90 && hsl2.h <= 150) && hsl2.s > 0.1;
    const isGray1 = hsl1.s < 0.1;
    const isGray2 = hsl2.s < 0.1;
    
    // If one is green and other is gray, add penalty
    if ((isGreen1 && isGray2) || (isGray1 && isGreen2)) {
      hueBonus = 25; // Penalty for green vs gray confusion
    }
    
    // If both are green, bonus for similar green hues
    if (isGreen1 && isGreen2) {
      const hueDistance = Math.min(
        Math.abs(hsl1.h - hsl2.h),
        360 - Math.abs(hsl1.h - hsl2.h)
      );
      if (hueDistance < 20) {
        hueBonus = -8; // Bonus for similar greens
      }
    }
    
    // Combine distances with hue-aware bonus
    return standardDistance * 0.6 + perceptualDistance * 0.4 + hueBonus;
  };
  
  // Calculate distances for all markers
  const markersWithDistance = markers.map(marker => {
    const markerRgb = hexToRgb(marker.hex);
    return {
      ...marker,
      distance: calculateEnhancedEuclideanDistance(targetRgb, markerRgb)
    };
  });
  
  // Sort by distance (closest first) and return top results
  return markersWithDistance
    .sort((a, b) => a.distance - b.distance)
    .slice(0, maxResults);
}

// Professional closest marker matching with Euclidean distance and debug logging
export function findClosestMarker(targetHex, markers) {
  if (!markers || markers.length === 0) return null;
  
  const targetRgb = hexToRgb(targetHex);
  const targetHsl = rgbToHsl(targetRgb.r, targetRgb.g, targetRgb.b);
  let bestMarker = markers[0];
  let bestDistance = Infinity;
  
  // DEBUG: Log target color analysis
  console.log(`🎯 TARGET COLOR: ${targetHex}`, {
    rgb: `R:${targetRgb.r} G:${targetRgb.g} B:${targetRgb.b}`,
    hsl: `H:${targetHsl.h.toFixed(1)} S:${(targetHsl.s*100).toFixed(1)}% L:${(targetHsl.l*100).toFixed(1)}%`
  });
  
  // Store top 5 candidates for debugging
  const debugCandidates = [];
  
  // Enhanced Euclidean distance with perceptual weighting for green vs gray
  const calculateEnhancedEuclideanDistance = (rgb1, rgb2) => {
    // Standard Euclidean distance
    const standardDistance = Math.sqrt(
      Math.pow(rgb1.r - rgb2.r, 2) +
      Math.pow(rgb1.g - rgb2.g, 2) +
      Math.pow(rgb1.b - rgb2.b, 2)
    );
    
    // Enhanced distance with perceptual weighting
    // Human eye is more sensitive to green, then red, then blue
    const perceptualDistance = Math.sqrt(
      Math.pow(rgb1.r - rgb2.r, 2) * 0.30 +  // Red weight
      Math.pow(rgb1.g - rgb2.g, 2) * 0.59 +  // Green weight (most important)
      Math.pow(rgb1.b - rgb2.b, 2) * 0.11    // Blue weight
    );
    
    // Additional hue-aware penalty to distinguish green from gray
    const hsl1 = rgbToHsl(rgb1.r, rgb1.g, rgb1.b);
    const hsl2 = rgbToHsl(rgb2.r, rgb2.g, rgb2.b);
    
    let hueBonus = 0;
    
    // Special handling for green vs gray distinction
    const isGreen1 = (hsl1.h >= 90 && hsl1.h <= 150) && hsl1.s > 0.1;
    const isGreen2 = (hsl2.h >= 90 && hsl2.h <= 150) && hsl2.s > 0.1;
    const isGray1 = hsl1.s < 0.1;
    const isGray2 = hsl2.s < 0.1;
    
    // If one is green and other is gray, add penalty
    if ((isGreen1 && isGray2) || (isGray1 && isGreen2)) {
      hueBonus = 30; // Penalty for green vs gray confusion
    }
    
    // If both are green, bonus for similar green hues
    if (isGreen1 && isGreen2) {
      const hueDistance = Math.min(
        Math.abs(hsl1.h - hsl2.h),
        360 - Math.abs(hsl1.h - hsl2.h)
      );
      if (hueDistance < 20) {
        hueBonus = -10; // Bonus for similar greens
      }
    }
    
    // Combine distances with hue-aware bonus
    return standardDistance * 0.6 + perceptualDistance * 0.4 + hueBonus;
  };
  
  // Find the best match with debugging
  markers.forEach(marker => {
    const markerRgb = hexToRgb(marker.hex);
    const markerHsl = rgbToHsl(markerRgb.r, markerRgb.g, markerRgb.b);
    const distance = calculateEnhancedEuclideanDistance(targetRgb, markerRgb);
    
    // Calculate additional info for debugging
    const hueDistance = Math.min(
      Math.abs(targetHsl.h - markerHsl.h),
      360 - Math.abs(targetHsl.h - markerHsl.h)
    );
    
    const debugInfo = {
      name: marker.name,
      hex: marker.hex,
      distance: distance.toFixed(2),
      rgb: `R:${markerRgb.r} G:${markerRgb.g} B:${markerRgb.b}`,
      hsl: `H:${markerHsl.h.toFixed(1)} S:${(markerHsl.s*100).toFixed(1)}% L:${(markerHsl.l*100).toFixed(1)}%`,
      hueDistance: hueDistance.toFixed(1) + '°',
      satDiff: Math.abs(targetHsl.s - markerHsl.s).toFixed(3),
      lightDiff: Math.abs(targetHsl.l - markerHsl.l).toFixed(3),
      // Debug green vs gray detection
      isGreen: ((markerHsl.h >= 90 && markerHsl.h <= 150) && markerHsl.s > 0.1),
      isGray: markerHsl.s < 0.1
    };
    
    debugCandidates.push(debugInfo);
    
    if (distance < bestDistance) {
      bestDistance = distance;
      bestMarker = marker;
    }
  });
  
  // DEBUG: Show top 5 candidates
  const topCandidates = debugCandidates
    .sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance))
    .slice(0, 5);
    
  console.log('🏆 TOP 5 ENHANCED EUCLIDEAN CANDIDATES:');
  topCandidates.forEach((candidate, index) => {
    const isWinner = index === 0;
    console.log(`${isWinner ? '👑' : `${index + 1}.`} ${candidate.name} (${candidate.hex})`, {
      enhancedDistance: candidate.distance,
      rgb: candidate.rgb,
      hsl: candidate.hsl,
      colorType: {
        isGreen: candidate.isGreen,
        isGray: candidate.isGray
      },
      differences: {
        hueDistance: candidate.hueDistance,
        saturationDiff: candidate.satDiff,
        lightnessDiff: candidate.lightDiff
      }
    });
  });
  
  console.log(`✅ FINAL SELECTION: ${bestMarker.name} (${bestMarker.hex}) with enhanced distance ${bestDistance.toFixed(2)}\n`);

  return bestMarker;
}

// Brands that use code_old/code_new instead of a single code field
const OLD_NEW_CODE_BRANDS = new Set(['Ohuhu', 'Ohuhu (Old)', 'Ohuhu (New)', 'Sanjoki']);

export function usesOldNewCodes(brand) {
  return OLD_NEW_CODE_BRANDS.has(brand);
}

export function getMarkerCode(marker, mode = 'both', fallback = '') {
  if (!marker) return fallback;
  if (!usesOldNewCodes(marker.brand)) return marker.code || fallback;
  if (mode === 'old') return marker.code_old || fallback;
  if (mode === 'new') return marker.code_new || fallback;
  const codes = [marker.code_old, marker.code_new].filter(Boolean);
  return codes.length > 0 ? codes.join(' / ') : fallback;
}
